/**
 * Single fetch wrapper for the backend.
 *
 * Behavior:
 * - Reads access token from localStorage and adds Authorization header.
 * - On 401, tries to refresh once using the refresh token, then retries the original request.
 * - All errors thrown as ApiError with `code` and `message` from the backend JSON.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

const ACCESS_KEY = 'altcoins.accessToken';
const REFRESH_KEY = 'altcoins.refreshToken';

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export const tokens = {
  getAccess: () => localStorage.getItem(ACCESS_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
  isMultipart?: boolean;
}

async function rawFetch(path: string, opts: ApiOptions): Promise<Response> {
  const headers: Record<string, string> = {
    ...((opts.headers as Record<string, string>) || {}),
  };
  if (!opts.isMultipart) {
    headers['Content-Type'] = 'application/json';
  }
  if (!opts.skipAuth) {
    const access = tokens.getAccess();
    if (access) headers['Authorization'] = `Bearer ${access}`;
  }

  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    body = opts.isMultipart ? (opts.body as FormData) : JSON.stringify(opts.body);
  }

  return fetch(`${API_BASE}${path}`, {
    method: opts.method || 'GET',
    headers,
    body,
    credentials: 'include',
  });
}

/** Attempt to refresh the access token. Returns true on success. */
async function tryRefresh(): Promise<boolean> {
  const refresh = tokens.getRefresh();
  if (!refresh) return false;
  try {
    const res = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: refresh }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (data?.accessToken && data?.refreshToken) {
      tokens.set(data.accessToken, data.refreshToken);
      return true;
    }
  } catch {
    return false;
  }
  return false;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  let res = await rawFetch(path, opts);

  if (res.status === 401 && !opts.skipAuth) {
    const ok = await tryRefresh();
    if (ok) {
      res = await rawFetch(path, opts);
    } else {
      tokens.clear();
      // Let the caller catch the error and redirect to login
    }
  }

  if (!res.ok) {
    let code = 'UNKNOWN';
    let message = `Request failed (${res.status})`;
    try {
      const data = await res.json();
      code = data?.error?.code ?? code;
      message = data?.error?.message ?? message;
    } catch {
      // Non-JSON error body
    }
    throw new ApiError(res.status, code, message);
  }

  // No content
  if (res.status === 204) return undefined as T;

  return res.json() as Promise<T>;
}
